/**
 * COPIED (with import modifications) FROM EndUserApp src/lib/logger/logger.js
 */

import debounce from 'lodash/debounce';

import { postRequest as post } from '../../http/lib';
import SessionStore from '../sessionStore';

export const LOG_BATCH_PATH = '/index.php?rm=box_gen204_batch_record';
export const BATCH_QUEUE_KEY = 'LOGGER_SERVICE_QUEUE';
const BATCH_TIMEOUT_MS = 5000;

interface LogEvent {
  [key: string]: unknown;
  client_ds: number;
}

interface BatchLogItem {
  category: string;
  event_type: string;
  events: LogEvent[];
}

/**
 * Class to send batched logs to gen204
 */
class BatchLogger {
  sessionStore = new SessionStore();

  willUseBeacon = navigator && typeof navigator.sendBeacon === 'function';

  constructor() {
    // Only flush if the browser is modern enough. Otherwise, to send the data, the user has another page
    if (this.willUseBeacon && typeof this.submitBatchLog.flush === 'function') {
      const flush = () => this.submitBatchLog.flush();
      window.addEventListener('beforeunload', flush);
      window.addEventListener('unload', flush);
    }

    // On page load, try to submit the existing batched log events that may have lingered from
    // previous page visits.
    this.submitBatchLog();
  }

  /**
   * Send a single log event to the batch queue. Will be sent to the server
   * before BATCH_TIMEOUT_MS is reached.
   * @see https://confluence.inside-box.net/display/ETO/Analytics for more info
   *
   * @param {string} category - identifies the major storage category for the log
   * @param {string} eventType - a type specifier to differentiate logs.
   * @param {Object} logEvent - an object with keys and values that are sent as url args in
   *                            a POST to the server for logging.
   * @returns {void}
   */
  sendBatchLog(
    category: string,
    eventType: string,
    logEvent: Record<string, unknown>,
  ) {
    // Attach a client-side datetime stamp
    const eventWithTimestamp: LogEvent = {
      ...logEvent,
      client_ds: Math.floor(new Date().getTime() / 1000),
    };

    this.pushToBatchQueue({
      category,
      event_type: eventType,
      events: [eventWithTimestamp],
    });

    this.submitBatchLog();
  }

  /**
   * Push an item to the batch queue
   *
   * @private
   * @param  {Object} item
   * @returns {void}
   */
  pushToBatchQueue(item: BatchLogItem) {
    const queue = this.sessionStore.getItem(BATCH_QUEUE_KEY) || [];
    queue.push(item);
    this.sessionStore.setItem(BATCH_QUEUE_KEY, queue);
  }

  /**
   * Submits the batched log requests and clears the queue
   *
   * @private
   * @returns {undefined}
   */
  submitBatchLog = debounce(() => {
    const batchQueue = this.sessionStore.getItem(BATCH_QUEUE_KEY);
    if (!batchQueue) {
      return;
    }

    if (this.willUseBeacon) {
      // @NOTE(jtan): This does not have the X-Box-Client-Name or the X-Box-Client-Version header since sendBeacon does not support custom headers
      // If supported, send the gen204 request as beacons. This allows the queue to be
      // flushed on unload. Regular async requests will NOT go through as expected if
      // they are sent with a regular XHR on unload. Beacons will.
      // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
      const success = navigator.sendBeacon(
        LOG_BATCH_PATH,
        JSON.stringify(batchQueue),
      );
      if (success) {
        this.sessionStore.removeItem(BATCH_QUEUE_KEY);
      }
    } else {
      // Otherwise, just send gen204s with regular XHRs. Clear the session store only on
      // success, so we minimize losing logs.
      post(LOG_BATCH_PATH, `data=${JSON.stringify(batchQueue)}`, {
        dataIsFormURLEncoded: true,
      }).then(() => {
        this.sessionStore.removeItem(BATCH_QUEUE_KEY);
      });
    }
  }, BATCH_TIMEOUT_MS);
}

export { BatchLogger };

// Export a singleton logger instance
export default new BatchLogger();
